// Sign-in page — email/password + Google. Matches the resort's luxury aesthetic.
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, ArrowRight } from "lucide-react";
import { useCustomerAuth, startGoogleSignIn } from "@/lib/customerAuth";

export default function SignIn() {
    const { user, login } = useCustomerAuth();
    const nav = useNavigate();
    const loc = useLocation();
    const from = loc.state?.from?.pathname || "/account";
    const [form, setForm] = useState({ email: "", password: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => { if (user) nav("/account", { replace: true }); }, [user, nav]);

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(form.email, form.password);
            toast.success("Welcome back to Serenity Cove");
            nav(from, { replace: true });
        } catch (err) {
            toast.error(err.response?.data?.detail || "Sign-in failed");
        } finally { setBusy(false); }
    };

    return (
        <section data-testid="signin-page" className="min-h-screen bg-ivory pt-24 pb-16 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10 pointer-events-none" style={{ backgroundImage: "radial-gradient(circle at 20% 40%, #d4af37 0%, transparent 40%), radial-gradient(circle at 80% 60%, #0b3a2e 0%, transparent 45%)" }} />
            <div className="relative max-w-md mx-auto px-6">
                <div className="text-center mb-10">
                    <div className="text-[10px] uppercase tracking-[0.32em] text-gold">Serenity Cove</div>
                    <h1 className="mt-4 font-display text-4xl md:text-5xl text-navy">Sign in</h1>
                    <p className="mt-3 text-sm text-navy/60">Access your reservations and Cove loyalty points.</p>
                </div>

                <form onSubmit={submit} className="bg-white border border-navy/10 rounded-2xl p-8 shadow-sm space-y-5" data-testid="signin-form">
                    <label className="block">
                        <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Email</span>
                        <div className="mt-2 relative">
                            <Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
                            <input
                                type="email"
                                required
                                autoComplete="email"
                                data-testid="signin-email"
                                value={form.email}
                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                className="w-full pl-9 pr-3 py-3 border border-navy/15 rounded-lg text-sm focus:border-gold focus:outline-none"
                            />
                        </div>
                    </label>
                    <label className="block">
                        <div className="flex items-center justify-between">
                            <span className="text-[10px] uppercase tracking-[0.22em] text-navy/60">Password</span>
                            <Link to="/forgot-password" className="text-[11px] text-navy/60 hover:text-gold">Forgot?</Link>
                        </div>
                        <div className="mt-2 relative">
                            <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/30" />
                            <input
                                type="password"
                                required
                                autoComplete="current-password"
                                data-testid="signin-password"
                                value={form.password}
                                onChange={(e) => setForm({ ...form, password: e.target.value })}
                                className="w-full pl-9 pr-3 py-3 border border-navy/15 rounded-lg text-sm focus:border-gold focus:outline-none"
                            />
                        </div>
                    </label>
                    <button
                        type="submit"
                        disabled={busy}
                        data-testid="signin-submit"
                        className="w-full inline-flex items-center justify-center gap-2 bg-navy text-ivory px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.22em] hover:bg-navy/90 disabled:opacity-60 transition-colors"
                    >
                        {busy ? "Signing in…" : "Sign in"} <ArrowRight size={13} />
                    </button>

                    <div className="relative py-1">
                        <div className="border-t border-navy/10" />
                        <span className="absolute inset-0 flex items-center justify-center">
                            <span className="bg-white px-3 text-[10px] uppercase tracking-[0.22em] text-navy/50">or</span>
                        </span>
                    </div>

                    <button
                        type="button"
                        onClick={startGoogleSignIn}
                        data-testid="signin-google"
                        className="w-full inline-flex items-center justify-center gap-3 bg-white border border-navy/20 text-navy px-6 py-3 rounded-full text-[11px] uppercase tracking-[0.16em] hover:border-gold hover:bg-navy/[.02] transition-colors"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24"><path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.29 1.48-1.14 2.73-2.4 3.58v3h3.87c2.26-2.09 3.58-5.17 3.58-8.82z"/><path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.94-2.91l-3.87-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"/><path fill="#FBBC05" d="M5.27 14.29c-.24-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"/><path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/></svg>
                        Continue with Google
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-navy/60">
                    New to Serenity Cove?{" "}
                    <Link to="/signup" className="text-gold hover:underline" data-testid="signin-to-signup">Create an account</Link>
                </p>
            </div>
        </section>
    );
}
